'use client'

import { PropsWithChildren, createContext, useContext, useState } from 'react'
import { UseBoundStore, StoreApi } from 'zustand'

export type BoundedStore<S> = UseBoundStore<StoreApi<S>>

const createStoreProvider = <T, S extends T>(
  init: (initialState?: T) => BoundedStore<S>,
) => {
  const StoreContext = createContext<BoundedStore<S> | null>(null)

  type StoreProviderProps = PropsWithChildren<{
    initialState?: T
  }>

  const StoreProvider = ({ children, initialState }: StoreProviderProps) => {
    const [store] = useState(() => init(initialState))

    return (
      <StoreContext.Provider value={store}>{children}</StoreContext.Provider>
    )
  }

  const useStore = () => {
    const useState = useContext(StoreContext)
    if (!useState) {
      throw new Error(
        'useStore must be used within a corresponding StoreProvider',
      )
    }
    return useState
  }

  return [StoreProvider, useStore] as const
}

export default createStoreProvider
